import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';
import { Table, TableCell, TableContainer, TableRow } from 'components/table';

import CampaignPaymentsStatus from '../status/CampaignPaymentsStatus';
import BulkVariablePaymentsModal from '../payouts/modals/variable-payments/BulkVariablePaymentsModal';

import { bulkEditVariablePayments } from 'modules/campaigns/reducers/variable-payments/variable-payments';

import { isManagedServiceSubscription } from 'utils/subscription';
import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import { getDisplayBudget } from 'utils/payments';

import './campaign-payments-invoice.scss';

import type { Campaign, CampaignOverview, Organization, Payment, Subcampaign, VariablePayment } from 'types';

applyTranslations(require('./campaign-payments-invoice.i18n.json'));

const TooltipWithBulkEditModal = withModal(Tooltip, {
  modalContent: BulkVariablePaymentsModal
});

type OwnProps = {
  campaign: Campaign,
  payments: Payment,
  subcampaignsList: Array<Subcampaign>,
  onVariablePaymentsUpdate: () => void
};

type StateProps = {
  campaignVariablePayments: Array<VariablePayment>,
  campaignVariablePaymentsIsLoading: boolean,
  campaignOverview: CampaignOverview,
  organization: Organization
};

type DispatchProps = {
  dispatchBulkEditVariablePayments: (b: number, a: Array<VariablePayment>) => Promise<any>
};

type Props = OwnProps & StateProps & DispatchProps;


export class CampaignPaymentsInvoice extends React.Component<Props> {
  getVariablePaymentValue(variablePaymentsSum?: number | null) {
    return renderPrice(variablePaymentsSum, { defaultValue: '—' });
  }

  handleBulkEditVariablePayments = (variablePayments: Array<VariablePayment>): void => {
    const { campaign, dispatchBulkEditVariablePayments, onVariablePaymentsUpdate } = this.props;
    const campaignId = (campaign && campaign.id) || null;

    if (campaignId) {
      dispatchBulkEditVariablePayments(campaignId, variablePayments)
        .then(() => onVariablePaymentsUpdate());
    }
  };

  renderEditIcon(): React.ReactNode {
    const { campaign, campaignOverview, campaignVariablePayments, campaignVariablePaymentsIsLoading, subcampaignsList } = this.props;

    const campaignBudgetInfo = {
      total: campaign.budget,
      remaining: campaignOverview.remainingBudget,
      used: campaignOverview.usedBudget
    };

    return (
      <div className="campaign-payments-invoice__icon">
        <TooltipWithBulkEditModal
          modalContentProps={{
            campaignName: campaign.name,
            isLoading: campaignVariablePaymentsIsLoading,
            campaignVariablePayments,
            subcampaignsList,
            campaignBudgetInfo,
            onBulkEditVariablePayments: this.handleBulkEditVariablePayments
          }}
          className="campaign-payments-invoice__tooltip"
          message={__t('collabary_common.basics.edit')}
          width="micro"
        >
          <SVGDynamicIcon
            name="pencil-custom-bg"
            width={13}
            height={12}
            customColor="grey"
            clickable
          />
        </TooltipWithBulkEditModal>
      </div>
    );
  }

  renderTableRow(payments: Payment): React.ReactNode {
    const { organization } = this.props;
    const isTrial = payments.isTrial;
    const variablePaymentsSum = payments.legallyBindingVariablePaymentsSum;
    const isManagedService = isManagedServiceSubscription(organization);

    const feeCellClasses = classnames({
      'campaign-payments-invoice__cell': true,
      'campaign-payments-invoice__cell--excluded': isTrial
    });

    const feeValueCellClasses = classnames({
      'campaign-payments-invoice__cell': true,
      'campaign-payments-invoice__cell--excluded-value': isTrial
    });

    return (
      <TableRow
        key="campaign-invoice-row"
        className="campaign-payments-invoice__table-row-data"
      >
        <TableCell
          width="fill"
        >
          <div className="campaign-payments-invoice__cell">
            {__t('campaign_payments_invoice.aggregated_budget')}
          </div>
          <div className="campaign-payments-invoice__cell">
            <Tooltip
              width="long"
              showQuestionMark
              classNameWrapper="campaign-payments-invoice__tooltip"
              message={__t('campaign_payments_invoice.variable_payments_tooltip')}
            >
              {__t('campaign_payments_invoice.variable_payments')}
            </Tooltip>
          </div>
          {
            !isManagedService && (
              <div className={feeCellClasses}>
                {__t('campaign_payments_invoice.fee', {
                  fee: payments.feePercentage
                })}
              </div>
            )
          }

        </TableCell>
        <TableCell
          width="small"
          align="right"
        >
          <div className="campaign-payments-invoice__cell">
            {getDisplayBudget(payments.agreedBudget)}
          </div>
          <div className="campaign-payments-invoice__cell">
            {this.getVariablePaymentValue(variablePaymentsSum)}
            {this.renderEditIcon()}
          </div>
          {
            !isManagedService && (
              <div className={feeValueCellClasses}>
                {getDisplayBudget(payments.feeValue || 0)}
              </div>
            )
          }
        </TableCell>
        <TableCell
          width="medium"
        >
          &nbsp;
        </TableCell>
      </TableRow>
    );
  }

  renderTableFooter(payments: Payment): React.ReactNode {
    return (
      <TableRow
        key="campaign-invoice-footer"
        className="campaign-payments-invoice__table-row-footer"
      >
        <TableCell
          width="fill"
        >
          {__t('campaign_payments_invoice.total_budget')}
        </TableCell>
        <TableCell
          width="small"
          align="right"
        >
          {getDisplayBudget(payments.campaignTotal || 0)}
        </TableCell>
        <TableCell
          width="medium"
          align="right"
        >
          <CampaignPaymentsStatus
            profileType="brand"
            status={payments.status}
          />
        </TableCell>
      </TableRow>
    );
  }

  render() {
    const { payments } = this.props;

    return (
      <section className="campaign-payments-invoice">
        <TableContainer>
          <Table
            style="volume"
          >
            {this.renderTableRow(payments)}
            {this.renderTableFooter(payments)}
          </Table>
        </TableContainer>
      </section>
    );
  }
}

const mapStateToProps = ({ campaignOverview, campaignVariablePayments, organizations }): StateProps => ({
  campaignVariablePaymentsIsLoading: campaignVariablePayments.isLoading,
  campaignVariablePayments: campaignVariablePayments.variablePayments.items,
  campaignOverview: campaignOverview.overview || {},
  organization: organizations.currentOrganization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchBulkEditVariablePayments: (campaignId: number, variablePayments: Array<VariablePayment>) => (
    dispatch(bulkEditVariablePayments(campaignId, variablePayments))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignPaymentsInvoice);
